import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStethoscope,
  faSearch,
  faFileExcel,
  faFilePdf,
  faDollarSign,
  faCoins,
  faUserMd,
  faHospital,
  faCalendarAlt,
  faCreditCard,
  faArrowLeft,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";

const BackButton = ({ to, text }) => (
  <Link to={to}>
    <button className="btn btn-sm sms-btn px-4 py-2">
      <ArrowBackIcon fontSize="small" className="me-2" />
      Back to {text}
    </button>
  </Link>
);

const AllServicesReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
    search: "",
    page: 1,
    per_page: 500
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const perPageOptions = [500, 1000, 2000];

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search.trim()) params.append("search", filters.search.trim());
      params.append("page", filters.page);
      params.append("per_page", filters.per_page);

      const res = await axios.get(`${config.smsUrl}/creport/all_services_report_v2`, { params });
      setReport(res.data);
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      console.error(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search + reset page to 1
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }));
      fetchReport();
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [filters.start_date, filters.end_date, filters.search]);

  // Fetch when page or per_page changes
  useEffect(() => {
    fetchReport();
  }, [filters.page, filters.per_page]);

  const { summary = {}, data = [], pagination = {} } = report || {};

  const formatSSP = (amount) => `SSP ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
  const formatUSD = (amount) => `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // SMART VISIT LINK — WORKS WITH YOUR CURRENT ROUTES
  const getVisitLink = (row) => {
    if (row.payment_type === "INSURANCE") {
      return `/invoice-details/${row.visit_id}/${row.provider_id || 0}/${row.claim_status || 0}/${row.provider_type || 'private'}`;
    }
    if (row.visit_type === '1') {
      return `/billingreceipt/${row.visit_id}`;
    }
    if (row.visit_type === '2') {
      const admissionId = row.admission_id && row.admission_id.trim() ? row.admission_id : row.visit_id;
      return `/viewinpatientbill/${row.visit_id}/${admissionId}/1`;
    }
    return `/billingreceipt/${row.visit_id}`;
  };
  
  const handleVisitClick = (row) => {
    const link = getVisitLink(row);
    window.open(link, "_blank");
  };

  return (
    <>
      <PageTitle title="All Services Report" />
      <SubHeader title="Reports" subTitle="Service Revenue • Search • Pagination">
        <FontAwesomeIcon icon={faStethoscope} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar />

        {/* FILTER BAR */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="row align-items-center g-3">
              <div className="col-auto">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-auto">
                <input type="date" className="form-control form-control-sm" value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
              </div>
              <div className="col-auto">
                <input type="date" className="form-control form-control-sm" value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
              </div>
              <div className="col">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search Service, Patient, Visit ID..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-auto">
                <select className="form-select form-select-sm" value={filters.per_page}
                  onChange={(e) => setFilters({ ...filters, per_page: parseInt(e.target.value), page: 1 })}>
                  {perPageOptions.map(n => <option key={n} value={n}>{n} rows</option>)}
                </select>
              </div>
              <div className="col-auto ms-auto text-end">
                <button className="btn btn-success btn-sm me-2">
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </button>
                <button className="btn btn-danger btn-sm">
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="row g-4 mb-5">
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-lg border-0 rounded-4" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faCalendarAlt} size="3x" className="mb-3" />
                <h5 className="fw-bold">Transactions</h5>
                <h2 className="display-5 fw-bold">{summary.total_transactions || 0}</h2>
                <small>Page {filters.page} / {pagination.total_pages || 1}</small>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-lg border-0 rounded-4" style={{ background: "linear-gradient(135deg, #f093fb, #f5576c)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faCoins} size="3x" className="mb-3" />
                <h5 className="fw-bold">Revenue (SSP)</h5>
                <h3 className="display-6 fw-bold">{formatSSP(summary.total_revenue_ssp)}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-lg border-0 rounded-4" style={{ background: "linear-gradient(135deg, #4facfe, #00f2fe)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faDollarSign} size="3x" className="mb-3" />
                <h5 className="fw-bold">Revenue (USD)</h5>
                <h3 className="display-6 fw-bold text-warning">{formatUSD(summary.total_revenue_usd)}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-lg border-0 rounded-4" style={{ background: "linear-gradient(135deg, #43e97b, #38f9d7)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faCreditCard} size="3x" className="mb-3" />
                <h5 className="fw-bold">Payment Split</h5>
                <div className="mt-3">
                  <div><strong>{summary.cash_count || 0}</strong> Cash</div>
                  <div><strong>{summary.insurance_count || 0}</strong> Insurance</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE + PAGINATION */}
        {loading ? (
          <div className="text-center py-5"><LoadingGif /></div>
        ) : data.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <h5>No services found</h5>
            <small>Try adjusting filters</small>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle table-sm">
                <thead className="table-dark text-white">
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Visit ID</th>
                    <th>Patient</th>
                    <th>Service</th>
                    <th>Dept</th>
                    <th>Qty</th>
                    <th>SSP</th>
                    <th>USD</th>
                    {/* <th>Curr</th> */}
                    <th>Pay</th>
                    <th>Insurer</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, i) => (
                    <tr key={i} className="cursor-pointer" onClick={() => handleVisitClick(item)} style={{ cursor: "pointer" }}>
                      <td><strong>{(filters.page - 1) * filters.per_page + i + 1}</strong></td>
                      <td>{new Date(item.sale_date).toLocaleDateString('en-GB')}</td>
                      <td><span className="badge bg-primary">{item.visit_id}</span></td>
                      <td className="text-info">{item.patient_name}</td>
                      <td><strong>{item.service_name}</strong></td>
                      <td className="text-muted small">{item.department_name}</td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-end text-primary fw-bold">{formatSSP(item.revenue_ssp)}</td>
                      <td className="text-end text-success fw-bold">
                        {item.revenue_usd > 0 ? formatUSD(item.revenue_usd) : "-"}
                      </td>
                      {/* <td><span className={`badge ${item.currency_sold === 'USD' ? 'bg-warning' : 'bg-light text-dark'}`}>
                        {item.currency_sold}
                      </span></td> */}
                      <td>
                        {item.payment_type === 'CASH' 
                          ? <span className="badge bg-success">CASH</span>
                          : <span className="badge bg-info">INS</span>
                        }
                      </td>
                      <td className="small">{item.insurance_provider}</td>
                      <td>
                        {item.patient_type === 'Inpatient' 
                          ? <FontAwesomeIcon icon={faHospital} className="text-danger" />
                          : <FontAwesomeIcon icon={faUserMd} className="text-success" />
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {pagination.total_pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted">
                  Showing {(filters.page - 1) * filters.per_page + 1} to{" "}
                  {Math.min(filters.page * filters.per_page, pagination.total_records)} of {pagination.total_records}
                </div>
                <div className="btn-group">
                  <button className="btn btn-outline-primary btn-sm" disabled={filters.page === 1}
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Prev
                  </button>
                  <button className="btn btn-primary btn-sm disabled">
                    Page {filters.page} / {pagination.total_pages}
                  </button>
                  <button className="btn btn-outline-primary btn-sm" disabled={filters.page === pagination.total_pages}
                    onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}>
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-4 text-muted small">
          Generated: {report?.generated_at || "-"} | Search: "{filters.search || "All"}"
        </div>
      </Container>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(AllServicesReport);